import { IOption } from "components/MainSelect/MainSelectInterfaces";
import { DisplayInOptionsEnum, allElements } from "constants/constants";
import { CorporateDashboardContext } from "contexts/CorporateDashboardContext/CorporateDashboardContext";
import { TrailingTrendPeriodOptionsEnum } from "entities/Corporations/CorporationsEntityInterfaces";
import { LocationsEntity } from "entities/Locations/LocationsEntity";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFirstDayOfTheMonthAtZeroHour, getTodayAtEndOfDay, objectComparator } from "utilities/utilities";

const firstDayOfTheMonth = getFirstDayOfTheMonthAtZeroHour();
const today = getTodayAtEndOfDay();

export interface ICorporateDashboardFilters {
    startDate?: Date | null;
    endDate?: Date | null;
    division?: string | number;
    region?: string | number;
    productionLine?: string | (number | string)[];
    machineCenter?: string | (number | string)[];
    trailingTrendPeriod?: string | number;
    displayIn?: string;
}

export const initialCorporateDashboardFiltersContext: ICorporateDashboardFilters = {
    startDate: firstDayOfTheMonth,
    endDate: today,
    division: allElements,
    region: allElements,
    productionLine: [allElements],
    machineCenter: [allElements],
    trailingTrendPeriod: TrailingTrendPeriodOptionsEnum.ONE_MONTH,
    displayIn: DisplayInOptionsEnum.PERCENT,
};

export const initialCorporateDashboardFilters: ICorporateDashboardFilters = {
    division: allElements,
    region: allElements,
    productionLine: [allElements],
    machineCenter: [allElements],
    trailingTrendPeriod: TrailingTrendPeriodOptionsEnum.ONE_MONTH,
};

export const validCorporateSummaryDefaultFields = [
    "productionLine",
    "machineCenter",
    "division",
    "region",
    "trailingTrendPeriod",
];

const useCorporateDashboardFilters = () => {
    const {
        handleSetFilters,
        filters: contextFilters,
        resetSelections,
        handleRemoveDefaultFilters,
        handleSetFiltersDefault,
        loadingDefaultOperations,
        userFiltersDefault,
    } = useContext(CorporateDashboardContext);
    const { corporateDashboardFiltersData, loading: loadingNewFilters } =
        LocationsEntity.GetCorporateDashboardFilters(contextFilters);
    const [filters, setFilters] = useState<ICorporateDashboardFilters>(contextFilters);
    const { t } = useTranslation();

    const allLabel = useMemo(() => t("corporate_dashboard.filters.all"), [t]);

    const divisionOptions = useMemo(() => {
        if (loadingNewFilters) return [{ id: allElements, name: allLabel }];
        let divisionsList: IOption[] = [];
        divisionsList =
            corporateDashboardFiltersData?.division?.map((division) => {
                return {
                    id: division.division_id,
                    name: division.name,
                } as IOption;
            }) || [];
        divisionsList?.unshift({ id: allElements, name: allLabel });
        return divisionsList;
    }, [allLabel, corporateDashboardFiltersData, loadingNewFilters]);

    const regionOptions = useMemo(() => {
        if (loadingNewFilters) return [{ id: allElements, name: allLabel }];
        let regionsList: IOption[] = [];
        regionsList =
            corporateDashboardFiltersData?.region
                ?.filter((region) =>
                    filters.division !== allElements ? region.division.division_id === filters.division : true,
                )
                .map((region) => {
                    return {
                        id: region.region_id,
                        name: region.name,
                    } as IOption;
                }) || [];
        regionsList?.unshift({ id: allElements, name: allLabel });
        return regionsList;
    }, [allLabel, corporateDashboardFiltersData, loadingNewFilters, filters.division]);

    const productionLinesOptions = useMemo(() => {
        if (loadingNewFilters) return [{ id: allElements, name: allLabel }];
        let productionLinesList: IOption[] = [];
        productionLinesList =
            corporateDashboardFiltersData?.production_line
                ?.filter((productionLine) =>
                    filters.region !== allElements && productionLine.mill.region
                        ? filters.region === productionLine.mill.region.region_id
                        : filters.division !== allElements && productionLine.mill.division
                        ? filters.division === productionLine.mill.division.division_id
                        : true,
                )
                .map((productionLine) => {
                    return {
                        id: productionLine.production_line_id,
                        name: productionLine.name,
                    } as IOption;
                }) || [];

        productionLinesList?.unshift({ id: allElements, name: allLabel });
        return productionLinesList;
    }, [allLabel, corporateDashboardFiltersData, loadingNewFilters, filters.region, filters.division]);

    const machineCentersOptions = useMemo(() => {
        if (loadingNewFilters) return [{ id: allElements, name: allLabel }];
        let machineCentersList: IOption[] = [];

        machineCentersList =
            corporateDashboardFiltersData?.machine_center
                ?.filter((machineCenter) =>
                    filters.productionLine?.length! > 0 &&
                    !filters.productionLine?.includes(allElements) &&
                    Array.isArray(filters.productionLine)
                        ? filters.productionLine?.find((el) => el === machineCenter.production_line.production_line_id)
                        : filters.region !== allElements && machineCenter.production_line.mill.region
                        ? filters.region === machineCenter.production_line.mill.region.region_id
                        : filters.division !== allElements && machineCenter.production_line.mill.division
                        ? filters.division === machineCenter.production_line.mill.division.division_id
                        : true,
                )
                .map((machineCenter) => {
                    return {
                        id: machineCenter.machine_center_id,
                        name: machineCenter.name,
                    } as IOption;
                }) || [];

        machineCentersList?.unshift({ id: allElements, name: allLabel });
        return machineCentersList;
    }, [
        allLabel,
        corporateDashboardFiltersData,
        loadingNewFilters,
        filters.productionLine,
        filters.region,
        filters.division,
    ]);

    const trailingTrendPeriodOptions = useMemo(
        () => [
            {
                id: TrailingTrendPeriodOptionsEnum.ONE_MONTH,
                name: t("corporate_dashboard.filters.trailing_trend_period.1_month"),
            },
            {
                id: TrailingTrendPeriodOptionsEnum.THIRTEEN_WEEK,
                name: t("corporate_dashboard.filters.trailing_trend_period.13_week"),
            },
            {
                id: TrailingTrendPeriodOptionsEnum.SIX_MONTH,
                name: t("corporate_dashboard.filters.trailing_trend_period.6_months"),
            },
            {
                id: TrailingTrendPeriodOptionsEnum.ONE_YEAR,
                name: t("corporate_dashboard.filters.trailing_trend_period.1_year"),
            },
        ],
        [t],
    );

    const handleFiltersChange = (newFilters: ICorporateDashboardFilters) => {
        if (Array.isArray(newFilters.productionLine)) {
            if (newFilters.productionLine.length <= 1) {
                newFilters.productionLine = [allElements];
            } else if (newFilters.productionLine.length > 0 && newFilters.productionLine.indexOf(allElements) === 0) {
                newFilters.productionLine = newFilters.productionLine.filter((productionLine) => {
                    return productionLine !== allElements;
                });
            } else if (newFilters.productionLine.length > 1 && newFilters.productionLine.includes(allElements)) {
                newFilters.productionLine = newFilters.productionLine.filter((productionLine) => {
                    return productionLine === allElements;
                });
            }
        }
        if (Array.isArray(newFilters.machineCenter)) {
            if (newFilters.machineCenter.length <= 1) {
                newFilters.machineCenter = [allElements];
            } else if (newFilters.machineCenter.length > 0 && newFilters.machineCenter.indexOf(allElements) === 0) {
                newFilters.machineCenter = newFilters.machineCenter.filter((machineCenter) => {
                    return machineCenter !== allElements;
                });
            } else if (newFilters.machineCenter.length > 1 && newFilters.machineCenter.includes(allElements)) {
                newFilters.machineCenter = newFilters.machineCenter.filter((machineCenter) => {
                    return machineCenter === allElements;
                });
            }
        }

        setFilters({ ...filters, ...newFilters });
        handleSetFilters({ ...contextFilters, ...newFilters });
        resetSelections();
    };

    const setFiltersDefault = async () => {
        try {
            await handleSetFiltersDefault(filters);
        } catch (error) {
            console.error(error);
        }
    };

    const isFiltersDefault = useMemo(() => {
        if (userFiltersDefault) {
            return objectComparator(userFiltersDefault, filters, validCorporateSummaryDefaultFields);
        }

        return false;
    }, [userFiltersDefault, filters]);

    useEffect(() => {
        if (userFiltersDefault) {
            setFilters((prev) => ({ ...prev, ...userFiltersDefault }));
        }
    }, [userFiltersDefault]);

    return {
        filters,
        setFilters,
        divisionOptions,
        regionOptions,
        productionLinesOptions,
        machineCentersOptions,
        trailingTrendPeriodOptions,
        handleFiltersChange,
        setFiltersDefault,
        handleRemoveDefaultFilters,
        isFiltersDefault,
        userFiltersDefault,
        loadingDefaultOperations,
    };
};

export default useCorporateDashboardFilters;
